import {
  getDealEventsAppOrdersDealsDealIdEventsGetOptions,
  getOrderAppOrdersOrderIdGetOptions,
} from '@/client/@tanstack/react-query.gen'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { useAnalytics } from '@/hooks/use-analytics'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, Calendar, ExternalLink, FileText, Mail, RefreshCw, User } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { DealTimeline } from './-components/deal-timeline'
import { useResolvedOrderFileUrl } from './-components/order-file-url'
import { buildApiUrl } from './-components/order-urls'
import { PdfDocumentViewer } from './-components/pdf-document-viewer'
import { StageTransitionPopover } from './-components/stage-transition-popover'

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr)
    return '—'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatCurrency(cents: number | string | null | undefined): string {
  if (cents === null || cents === undefined || cents === '') {
    return '—'
  }
  // ``total_value_cents`` is a JSON-serialized Decimal string off the API
  // (Numeric column on the deal). Coerce once for formatting.
  const numeric = typeof cents === 'string' ? Number(cents) : cents
  if (!Number.isFinite(numeric)) {
    return '—'
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric / 100)
}

function DealDetailHeader({
  dealId,
}: {
  dealId: number
}) {
  return (
    <div className="flex items-center gap-2 px-4 pt-4 lg:px-6 md:pt-6">
      <Button asChild variant="ghost" size="sm" className="h-7 px-2">
        <Link to="/orders/pipeline">
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Back to pipeline
        </Link>
      </Button>
      <span className="text-xs text-muted-foreground">
        Deal #
        {dealId}
      </span>
    </div>
  )
}

function DealLoading() {
  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="h-8 w-1/3 animate-pulse rounded-md bg-muted/40" />
      <div className="h-32 w-full animate-pulse rounded-md bg-muted/40" />
      <div className="h-64 w-full animate-pulse rounded-md bg-muted/40" />
    </div>
  )
}

function DealErrorPanel({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 p-12 text-center">
      <AlertTriangle className="h-6 w-6 text-destructive" />
      <div>
        <p className="text-sm font-medium">Could not load this deal</p>
        <p className="mt-1 text-sm text-muted-foreground">Try again in a moment.</p>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    </div>
  )
}

export const Route = createFileRoute('/_authed/_user/orders/pipeline/$dealId')({
  beforeLoad: ({ context }) => {
    if (!context.auth.canUseOrderInbox) {
      throw redirect({ to: '/' })
    }
    return {
      getTitle: () => 'Deal Detail',
    }
  },
  component: DealDetailPage,
})

function DealDetailPage() {
  const { dealId: rawDealId } = Route.useParams()
  // Path param arrives as string; coerce once at the boundary.
  const dealId = Number.parseInt(rawDealId, 10)
  const { capture } = useAnalytics()
  const viewedRef = useRef(false)

  const { data, isLoading, isError, refetch } = useQuery({
    ...getDealEventsAppOrdersDealsDealIdEventsGetOptions({
      path: { deal_id: dealId },
    }),
    staleTime: 15_000,
    enabled: Number.isFinite(dealId),
  })

  useEffect(() => {
    if (Number.isNaN(dealId) || viewedRef.current || !data)
      return
    viewedRef.current = true
    capture('orders_deal_detail_viewed', { deal_id: dealId })
  }, [capture, data, dealId])

  if (Number.isNaN(dealId)) {
    return (
      <div className="p-12 text-center text-sm text-muted-foreground">
        Invalid deal id.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <DealDetailHeader dealId={dealId} />

      {isLoading && <DealLoading />}
      {isError && <DealErrorPanel onRetry={() => { void refetch() }} />}

      {data && (
        <div className="grid grid-cols-1 gap-6 px-4 pb-6 lg:grid-cols-[3fr_2fr] lg:px-6">
          {/* Left column: latest document preview. */}
          <Card className="min-w-0 overflow-hidden">
            <CardHeader className="border-b p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Latest document</h3>
                {data.deal.latestOrderPublicId && (
                  <Button asChild variant="outline" size="sm" className="h-7">
                    <Link to="/orders/$orderId" params={{ orderId: data.deal.latestOrderPublicId }}>
                      Open in inbox
                    </Link>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {data.deal.latestOrderPublicId
                ? <LatestDocumentPreview orderId={data.deal.latestOrderPublicId} />
                : (
                    <div className="flex min-h-70 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
                      <Mail className="h-10 w-10 text-muted-foreground/50" />
                      <p>This deal has no extracted document yet.</p>
                    </div>
                  )}
            </CardContent>
          </Card>

          {/* Right column: hero summary + timeline. */}
          <div className="flex min-w-0 flex-col gap-4">
            <Card className="overflow-hidden">
              <CardHeader className="border-b p-4">
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-base font-semibold">
                      {data.deal.title ?? `Deal #${data.deal.id}`}
                    </h2>
                    <StageTransitionPopover
                      dealId={data.deal.id}
                      stage={data.deal.stage}
                      allowedTransitions={data.deal.allowedTransitions}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {data.deal.customerName ?? data.deal.customerEmail ?? 'Unknown customer'}
                  </p>
                </div>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3 p-4 text-sm">
                <DetailField icon={User} label="Assignee" value={data.deal.assignee?.name ?? '—'} />
                <DetailField icon={Calendar} label="Deadline" value={formatDate(data.deal.requestedDelivery)} />
                <DetailField icon={FileText} label="Documents" value={String(data.deal.documentCount)} />
                <DetailField icon={Calendar} label="Value" value={formatCurrency(data.deal.totalValueCents)} />
                {data.deal.shipTo && (
                  <DetailField
                    icon={Mail}
                    label="Ship-to"
                    value={data.deal.shipTo}
                    className="col-span-2"
                  />
                )}
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <CardHeader className="border-b p-4">
                <h3 className="text-sm font-medium">Activity</h3>
                <p className="text-xs text-muted-foreground">
                  Newest first. Documents, emails, and stage transitions.
                </p>
              </CardHeader>
              <CardContent className="p-4">
                <DealTimeline events={data.events} />
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  )
}

function DetailField({
  icon: Icon,
  label,
  value,
  className = '',
}: {
  icon: typeof FileText
  label: string
  value: string
  className?: string
}) {
  return (
    <div className={`flex flex-col gap-0.5 ${className}`}>
      <span className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </span>
      <span
        className="truncate text-sm text-foreground"
        title={value}
      >
        {value}
      </span>
    </div>
  )
}

function LatestDocumentPreview({ orderId }: { orderId: string }) {
  const { data: order, isLoading, isError, refetch } = useQuery({
    ...getOrderAppOrdersOrderIdGetOptions({ path: { order_id: orderId } }),
    staleTime: 30_000,
  })
  const attachment = order?.attachments[0]
  const previewApiUrl = order?.previewImageUrl ? buildApiUrl(order.previewImageUrl) : undefined
  const attachmentApiUrl = attachment ? buildApiUrl(attachment.signedUrl) : undefined
  const resolvedPreviewUrl = useResolvedOrderFileUrl(previewApiUrl)
  const resolvedAttachmentUrl = useResolvedOrderFileUrl(
    attachment?.mimeType === 'application/pdf' ? undefined : attachmentApiUrl,
  )

  if (isLoading) {
    return (
      <div className="flex min-h-70 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <RefreshCw className="h-5 w-5 animate-spin" />
        Loading document...
      </div>
    )
  }

  if (isError || !order) {
    return (
      <div className="flex min-h-70 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
        <AlertTriangle className="h-6 w-6 text-destructive" />
        <p>Could not load document.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void refetch()
          }}
        >
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    )
  }

  const previewUrl = resolvedPreviewUrl.url ?? previewApiUrl
  const attachmentUrl = resolvedAttachmentUrl.url ?? attachmentApiUrl

  // Multi-page PDF rendering — pages are rasterized server-side and the
  // viewer requests one PNG per page from <attachmentApiUrl>/pages/<n>.
  // Non-PDF attachments fall back to the single-page preview image rendered
  // server-side.
  if (attachment && attachmentApiUrl && attachment.mimeType === 'application/pdf') {
    return (
      <PdfDocumentViewer
        url={attachmentApiUrl}
        filename={attachment.filename}
        pageCount={attachment.pageCount}
      />
    )
  }

  if (previewApiUrl && resolvedPreviewUrl.isLoading && !resolvedPreviewUrl.url) {
    return (
      <div className="flex min-h-70 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <RefreshCw className="h-5 w-5 animate-spin" />
        Loading preview...
      </div>
    )
  }

  if (previewUrl) {
    return (
      <a
        href={attachmentUrl ?? previewUrl}
        target="_blank"
        rel="noreferrer"
        className="block overflow-hidden rounded-md border bg-muted/20 transition-colors hover:bg-muted/40"
      >
        <img
          src={previewUrl}
          alt={attachment?.filename ?? 'Document preview'}
          className="mx-auto w-full object-contain"
          loading="lazy"
        />
        {attachment && (
          <div className="flex items-center justify-center gap-1 border-t bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
            <ExternalLink className="h-3 w-3" />
            Click to open full attachment
          </div>
        )}
      </a>
    )
  }

  if (order.bodyText) {
    return (
      <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm leading-relaxed font-sans">
        {order.bodyText}
      </pre>
    )
  }

  return (
    <div className="flex min-h-70 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
      <FileText className="h-10 w-10 text-muted-foreground/50" />
      <p>Preview unavailable for this document.</p>
    </div>
  )
}
